import moment from 'moment-timezone'

/**
 * Check if a date string contains time components
 * @param dateStr - Date string to check
 * @returns boolean indicating if the date string contains time
 *
 */
type DateString = string | Date | null | undefined
const hasTimeComponent = (dateStr: DateString): boolean => {
  if (!dateStr) return false
  if (dateStr instanceof Date) {
    // For Date objects, check if time is not midnight
    return (
      dateStr.getHours() !== 0 ||
      dateStr.getMinutes() !== 0 ||
      dateStr.getSeconds() !== 0
    )
  }
  // Check if string contains time components (HH:mm or HH:mm:ss)
  return /\d{1,2}:\d{2}(:\d{2})?/.test(dateStr)
}

/**
 * Format date with browser's timezone
 * @param date - Date string or Date object
 * @param format - Output format (default: 'MM/DD/YYYY HH:mm')
 * @param preserveTime - Whether to preserve the time component (optional, auto-detected if not provided)
 * @returns Formatted date string in local timezone
 */
export const formatLocalDate = (
  date: DateString,
  format = 'MM/DD/YYYY HH:mm',
  preserveTime?: boolean
): string => {
  if (!date) return ''
  const momentDate = moment(date)
  const shouldPreserveTime = preserveTime ?? hasTimeComponent(date)

  if (!shouldPreserveTime) {
    momentDate.startOf('day')
  }
  return momentDate.local().format(format)
}

/**
 * Format local date with time zone abbreviation (e.g., CST, IST)
 * @param date - Date string or Date object
 * @param format - Date-time format without time zone (default: 'MM/DD/YYYY HH:mm')
 * @param preserveTime - Whether to preserve time component
 * @returns Formatted string like '05/10/2025 14:45 IST'
 */
export const formatLocalDateWithZone = (
  date: string | Date | undefined | null,
  format = 'MM/DD/YYYY HH:mm',
  preserveTime?: boolean
): string => {
  if (!date) return ''

  const browserTimeZone = moment.tz.guess()

  const momentDate = moment.tz(date, browserTimeZone)
  const shouldPreserveTime = preserveTime ?? hasTimeComponent(date)

  if (!shouldPreserveTime) {
    momentDate.startOf('day')
  }

  const formattedDate = momentDate.format(format)
  let timeZoneAbbrievate = momentDate.format('z')

  // if timezone abbrievate gives me an offset
  const offset4DigitPattern = /^[+-]\d{4}$/
  const offset2DigitPattern = /^[+-]\d{2}$/

  if (offset4DigitPattern.test(timeZoneAbbrievate)) {
    const sign = timeZoneAbbrievate[0]
    const hours = timeZoneAbbrievate.slice(1, 3)
    const minutes = timeZoneAbbrievate.slice(3, 5)
    timeZoneAbbrievate = `(UTC ${sign}${hours}:${minutes})`
  } else if (offset2DigitPattern.test(timeZoneAbbrievate)) {
    const sign = timeZoneAbbrievate[0]
    const hours = timeZoneAbbrievate.slice(1, 3)
    timeZoneAbbrievate = `(UTC ${sign}${hours}:00)`
  }

  return `${formattedDate} ${timeZoneAbbrievate}`
}

/**
 * Format date to UTC
 * @param date - Date string or Date object
 * @param format - Output format (default: 'YYYY-MM-DD HH:mm:ss')
 * @param preserveTime - Whether to preserve the time component (optional, auto-detected if not provided)
 * @returns Formatted date string in UTC
 */
export const formatUTCDate = (
  date: DateString,
  format = 'YYYY-MM-DD HH:mm:ss',
  preserveTime?: boolean
): string => {
  if (!date) return ''
  const momentDate = moment(date)
  const shouldPreserveTime = preserveTime ?? hasTimeComponent(date)

  if (!shouldPreserveTime) {
    momentDate.startOf('day')
  }
  return momentDate.utc().format(format)
}

/**
 * Get current date in local timezone
 * @param format - Output format (default: 'MM/DD/YYYY HH:mm')
 * @param preserveTime - Whether to preserve the time component (default: true)
 * @returns Current date string in local timezone
 */
export const getCurrentLocalDate = (
  format = 'MM/DD/YYYY HH:mm',
  preserveTime = true
): string => {
  const momentDate = moment()
  if (!preserveTime) {
    momentDate.startOf('day')
  }
  return momentDate.local().format(format)
}

/**
 * Get current date in UTC
 * @param format - Output format (default: 'YYYY-MM-DD HH:mm:ss')
 * @param preserveTime - Whether to preserve the time component (default: true)
 * @returns Current date string in UTC
 */
export const getCurrentUTCDate = (
  format = 'YYYY-MM-DD HH:mm:ss',
  preserveTime = true
): string => {
  const momentDate = moment()
  if (!preserveTime) {
    momentDate.startOf('day')
  }
  return momentDate.utc().format(format)
}

/**
 * Add days to a date in local timezone
 * @param date - Date string or Date object
 * @param days - Number of days to add
 * @param format - Output format (default: 'MM/DD/YYYY HH:mm')
 * @param preserveTime - Whether to preserve the time component (optional, auto-detected if not provided)
 * @returns Formatted date string with added days in local timezone
 */
export const addDaysToDate = (
  date: string | Date,
  days: number,
  format = 'MM/DD/YYYY HH:mm',
  preserveTime?: boolean
): string => {
  const momentDate = moment(date)
  const shouldPreserveTime = preserveTime ?? hasTimeComponent(date)

  if (!shouldPreserveTime) {
    momentDate.startOf('day')
  }
  return momentDate.local().add(days, 'days').format(format)
}

/**
 * Subtract days from a date in local timezone
 * @param date - Date string or Date object
 * @param days - Number of days to subtract
 * @param format - Output format (default: 'MM/DD/YYYY HH:mm')
 * @param preserveTime - Whether to preserve the time component (optional, auto-detected if not provided)
 * @returns Formatted date string with subtracted days in local timezone
 */
export const subtractDaysFromDate = (
  date: string | Date,
  days: number,
  format = 'MM/DD/YYYY HH:mm',
  preserveTime?: boolean
): string => {
  const momentDate = moment(date)
  const shouldPreserveTime = preserveTime ?? hasTimeComponent(date)

  if (!shouldPreserveTime) {
    momentDate.startOf('day')
  }
  return momentDate.local().subtract(days, 'days').format(format)
}

/**
 * Compare two dates
 * @param date1 - First date
 * @param date2 - Second date
 * @param compareTime - Whether to include time in comparison (optional, auto-detected if not provided)
 * @returns negative if date1 < date2, 0 if equal, positive if date1 > date2
 */
export const compareDates = (
  date1: string | Date,
  date2: string | Date,
  compareTime?: boolean
): number => {
  const moment1 = moment(date1)
  const moment2 = moment(date2)

  const shouldCompareTime =
    compareTime ?? (hasTimeComponent(date1) || hasTimeComponent(date2))

  if (!shouldCompareTime) {
    moment1.startOf('day')
    moment2.startOf('day')
  }

  return moment1.valueOf() - moment2.valueOf()
}

/**
 * Format time only in local timezone
 * @param time - Time string
 * @param inputFormat - Input time format (default: 'HH:mm')
 * @param outputFormat - Output time format (default: 'HH:mm')
 * @returns Formatted time string
 */
export const formatLocalTime = (
  time: string,
  inputFormat = 'HH:mm',
  outputFormat = 'HH:mm'
): string => {
  return moment(time, inputFormat).local().format(outputFormat)
}

/**
 * Get start and end of week in local timezone
 * @param format - Output format (default: 'YYYY-MM-DD HH:mm:ss')
 * @param preserveTime - Whether to preserve the time component (default: false)
 * @returns Object with start and end dates of current week
 */
export const getWeekBoundaries = (
  format = 'YYYY-MM-DD HH:mm:ss',
  preserveTime = false
) => {
  const start = moment().local().startOf('week')
  const end = moment().local().endOf('week')

  if (!preserveTime) {
    start.startOf('day')
    end.endOf('day')
  }

  return {
    start: start.format(format),
    end: end.format(format),
  }
}

/**
 * Check if a date is valid
 * @param date - Date to validate
 * @returns boolean indicating if date is valid
 */
export const isValidDate = (date: string | Date): boolean => {
  return moment(date).isValid()
}
